import React, {
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
  useCallback,
  RefObject,
  FocusEvent,
  ChangeEvent,
  KeyboardEvent,
} from 'react';
import { IconBaseProps } from 'react-icons';
import { useField, FormHandles } from '@unform/core';
import { mask as onMask } from '../../utils/inputmask';

import {
  Root,
  ContainerInput,
  ContainerCheck,
  ContainerError,
  ContentInputandLabel
} from './styles';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  icon?: React.ComponentType<IconBaseProps>;
  IconLeft?: boolean | true;
  BgColor?: string;
  Color?: string;
  Padding?: string;
  Border?: string;
  BorderBottomRadiusNo?: boolean;
  titleCheck?: string;
  width?: string;
  textLabel?: string;
  mask?: string;
  formRef?: RefObject<FormHandles>;
  handledInputBlurExt?: (value: string) => void;
  handledInputFocusExt?: (event : FocusEvent<HTMLInputElement>) => void;
  onChangeExt?: (event : ChangeEvent<HTMLInputElement>) => void;
}

const Input: React.FC<InputProps> = ({
  name,
  icon: Icon,
  IconLeft,
  BgColor,
  Padding,
  Color,
  Border,
  BorderBottomRadiusNo,
  titleCheck,
  textLabel,
  mask,
  width,
  formRef,
  handledInputBlurExt,
  handledInputFocusExt,
  onChangeExt,
  ...rest
} : InputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const { fieldName, defaultValue, error, registerField } = useField(name);
  const [isBackspace, setIsBackspace] = useState(false);

  const handledInputBlur = useCallback(() => {
    setIsFocused(false);
    setIsFilled(!!inputRef.current?.value);
    if (handledInputBlurExt && inputRef.current?.value)
    handledInputBlurExt(inputRef.current?.value);
  }, [handledInputBlurExt]);

  const handledInputFocus = useCallback((event : FocusEvent<HTMLInputElement>) => {
    if (handledInputFocusExt)
    handledInputFocusExt(event);
    setIsFocused(true);
  }, [handledInputFocusExt]);

  useEffect(() => {
    registerField<String>({
      name: fieldName,
      ref: inputRef.current,
      getValue(ref) {
        return ref.value;
      },
      setValue(ref, value) {
        if (mask)
        ref.value = onMask(value as string, mask);
        else
        ref.value = value;
      }
    });
  }, [fieldName, registerField, mask]);

  return (
    <Root>
      {!titleCheck && (
        <ContentInputandLabel>
          <label htmlFor={name}>{textLabel}</label>
          <ContainerInput
            isErrored={!!error}
            isFilled={isFilled}
            isFocused={isFocused}
            BgColor={BgColor}
            Color={Color}
            Padding={Padding}
            Border={Border}
            width={width}
            BorderBottomRadiusNo={BorderBottomRadiusNo}
            error={!!error}
            disabled={rest.disabled}
          >
            {Icon && IconLeft && <Icon />}
            <input
              name={name}
              style={{ width }}
              ref={inputRef}
              onKeyUp={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Backspace') setIsBackspace(true);
                else setIsBackspace(false);

                if (e.key === 'Enter' && handledInputBlurExt) handledInputBlurExt(e.currentTarget.value);
              }}
              onChange={(e : ChangeEvent<HTMLInputElement>) =>  {
                let valueInput = e.currentTarget.value;
                const valueInputArray = e.currentTarget.value.split('');
                var set = valueInputArray.indexOf('_') + 1;
                if (onChangeExt) {
                  onChangeExt(e)
                }
                                                           
                if (isBackspace) {
                  set = e.currentTarget.selectionStart || 0;
                }
                
                if (formRef && formRef.current) {
                  if (mask) {
                    valueInput = onMask(valueInput, mask)
                    formRef.current.setFieldValue(name, valueInput);
                  }
                  if (onChangeExt) {
                    onChangeExt(e)
                  }
                  const error = formRef.current.getFieldError(name);
                  if (error) formRef.current.setFieldError(name, '');
                  if (set && mask) {
                    e.target.setSelectionRange(set, set);
                  }
                }
              }}
              {...rest}
              onFocus={(e) => handledInputFocus(e)}
              onBlur={handledInputBlur}
            />

            {Icon && !IconLeft && <Icon />}
          </ContainerInput>
        </ContentInputandLabel>
      )}
      {titleCheck && (
        <ContainerCheck>
          <input
            type="checkbox"
            defaultValue={defaultValue}
            ref={inputRef}
            {...rest}
            onFocus={handledInputFocus}
            onBlur={handledInputBlur}
          />
          <span>{titleCheck}</span>
        </ContainerCheck>
      )}
      {error && (
        <ContainerError>
          <span>{error}</span>
        </ContainerError>
      )}
    </Root>
  );
};

export default Input;
